import React from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { isLocked } from "@/utils/subscription";
import { CrownIcon } from "@/components/LockBadge";
import { X, Users, Heart, BarChart3, Salad, Crown, HelpCircle, Shield, FileText, Headphones, ChefHat, Home, Settings, LogOut } from "lucide-react";

const menuItems = [
  { key: "home", icon: Home, path: "/" },
  { key: "familyAccess", icon: Users, path: null, hasCrown: false, comingSoon: true },
  { key: "Favorites", icon: Heart, path: "/dishes", isCapitalized: true },
  { key: "smartInsights", icon: BarChart3, path: "/insights" },
  { key: "nutritionMode", icon: Salad, path: null, comingSoon: true },
  { key: "settingsTitle", icon: Settings, path: "/settings" },
  { key: "subscription", icon: Crown, path: "/subscription" },
  { key: "faq", icon: HelpCircle, path: "/faq", comingSoon: false },
  { key: "privacy", icon: Shield, path: "/privacy", comingSoon: false },
  { key: "terms", icon: FileText, path: "/terms", comingSoon: false },
  { key: "support", icon: Headphones, path: "/support", comingSoon: false },
];

export default function SideMenu({ open, onClose }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const locked = isLocked(user);
  const [toast, setToast] = React.useState(null);

  const showComingSoon = () => {
    setToast(t("comingSoon") || "Незабаром");
    setTimeout(() => setToast(null), 2000);
  };

  const handleLogout = () => {
    logout();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10000]" data-testid="side-menu">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute left-0 top-0 bottom-0 w-80 bg-[#FAFAF8] border-r-4 border-[#7B9E7F] rounded-r-2xl overflow-hidden flex flex-col animate-slide-right">
        {/* Header (fixed) */}
        <div className="p-6 border-b border-[#F2E9E4] flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#7B9E7F] flex items-center justify-center">
                <ChefHat className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <span className="font-heading text-xl text-[#3D4035]">Meal Pill</span>
            </div>
            <button
              data-testid="side-menu-close"
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-[#F2E9E4] flex items-center justify-center"
            >
              <X className="w-4 h-4 text-[#6B705C]" strokeWidth={1.5} />
            </button>
          </div>
          <p className="font-body text-[#6B705C]">{user?.name}</p>
          <p className="font-body text-sm text-[#A5A58D]">{user?.email}</p>
        </div>

        {/* Menu Items (scrollable) */}
        <div className="flex-1 py-2 overflow-y-auto min-h-0">
          {toast && (
            <div className="mx-4 mb-2 px-4 py-2 bg-[#3D4035] text-white text-sm font-body rounded-xl text-center animate-fade-in">
              {toast}
            </div>
          )}
          {menuItems.map(({ key, icon: Icon, path, hasCrown, isCapitalized, comingSoon }) => (
            <button
              key={key}
              data-testid={`side-menu-${key}`}
              onClick={() => {
                const effPath = (key === "smartInsights" && locked) ? "/subscription" : path;
                if (effPath) { navigate(effPath); onClose(); }
                else if (comingSoon) showComingSoon();
                else onClose();
              }}
              className="w-full px-6 py-3.5 flex items-center gap-3 text-left hover:bg-[#F2E9E4] transition-colors"
            >
              <Icon className={`w-5 h-5 ${comingSoon && !hasCrown ? "text-[#A5A58D]" : "text-[#6B705C]"}`} strokeWidth={1.5} />
              <span className={`font-body font-medium flex-1 ${comingSoon && !hasCrown ? "text-[#A5A58D]" : "text-[#3D4035]"}`}>
                {isCapitalized ? t("favorites").charAt(0).toUpperCase() + t("favorites").slice(1) : t(key)}
              </span>
              {key === "smartInsights" && locked && <CrownIcon className="ml-1" />}
              {hasCrown && (
                <Crown className="w-4 h-4 text-[#E9C46A]" strokeWidth={1.5} />
              )}
              {comingSoon && !hasCrown && (
                <span className="text-[10px] font-body font-bold text-[#A5A58D] bg-[#F2E9E4] px-2 py-0.5 rounded-full uppercase tracking-wide">
                  {t("comingSoon") || "Скоро"}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Footer: Logout (always visible) + Version */}
        <div className="flex-shrink-0 border-t border-[#F2E9E4]">
          <button
            data-testid="side-menu-logout"
            onClick={handleLogout}
            className="w-full px-6 py-3.5 flex items-center gap-3 text-left hover:bg-[#F2E9E4] transition-colors"
          >
            <LogOut className="w-5 h-5 text-[#B85C5C]" strokeWidth={1.5} />
            <span className="font-body font-medium text-[#B85C5C]">{t("logout")}</span>
          </button>
          <div className="px-6 py-3 border-t border-[#F2E9E4]">
            <p className="font-body text-xs text-[#A5A58D]">{t("appVersion")} 1.0.0</p>
          </div>
        </div>
      </div>
    </div>
  );
}
